// Per-user API keys, backed by the encrypted server store but exposed through a synchronous
// in-memory cache. The cache is hydrated once after login (hydrateKeysFromServer), so the
// many existing synchronous callers (loadAnthropicKey(), etc.) keep working unchanged.
import { fetchApiKeys, putApiKeys, type ApiKeys } from "./authClient";

// Legacy localStorage keys (pre-login era) — used only for one-time migration to the server.
const LEGACY = {
  ahrefs: "seo_settings_ahrefs_key",
  anthropic: "seo_settings_anthropic_key",
  dfsLogin: "seo_settings_dfs_login",
  dfsPassword: "seo_settings_dfs_password",
};

let cache: ApiKeys = { anthropic: "", ahrefs: "", dfsLogin: "", dfsPassword: "", brightdataToken: "", brightdataZone: "" };

// Pull the user's keys into the cache. If the server has none but legacy localStorage values
// exist, push them up once (migration) so existing users don't lose their keys.
export async function hydrateKeysFromServer(): Promise<void> {
  let keys: ApiKeys;
  try {
    keys = await fetchApiKeys();
  } catch {
    return; // not authenticated / offline — leave cache empty
  }
  const hasServer = !!(keys.anthropic || keys.ahrefs || keys.dfsLogin || keys.dfsPassword || keys.brightdataToken || keys.brightdataZone);
  if (!hasServer) {
    const legacy: Partial<ApiKeys> = {};
    try {
      const a = localStorage.getItem(LEGACY.anthropic); if (a) legacy.anthropic = a;
      const h = localStorage.getItem(LEGACY.ahrefs); if (h) legacy.ahrefs = h;
      const l = localStorage.getItem(LEGACY.dfsLogin); if (l) legacy.dfsLogin = l;
      const p = localStorage.getItem(LEGACY.dfsPassword); if (p) legacy.dfsPassword = p;
    } catch { /* ignore */ }
    if (Object.keys(legacy).length) {
      try {
        await putApiKeys(legacy);
        keys = { ...keys, ...legacy } as ApiKeys;
      } catch { /* ignore — keep whatever the server returned */ }
    }
  }
  cache = keys;
}

export function clearKeys(): void {
  cache = { anthropic: "", ahrefs: "", dfsLogin: "", dfsPassword: "", brightdataToken: "", brightdataZone: "" };
}

// ── Synchronous getters (read the cache) ──────────────────────
export function getAnthropicKey(): string { return cache.anthropic; }
export function getAhrefsKey(): string { return cache.ahrefs; }
export function getDfsCreds(): { login: string; password: string } { return { login: cache.dfsLogin, password: cache.dfsPassword }; }
export function getBrightDataCreds(): { token: string; zone: string } { return { token: cache.brightdataToken, zone: cache.brightdataZone }; }

// ── Async setters (persist to server, then update cache) ──────
export async function saveAnthropicKey(v: string): Promise<void> {
  await putApiKeys({ anthropic: v });
  cache = { ...cache, anthropic: v };
}
export async function saveAhrefsKey(v: string): Promise<void> {
  await putApiKeys({ ahrefs: v });
  cache = { ...cache, ahrefs: v };
}
export async function saveDfsCreds(login: string, password: string): Promise<void> {
  await putApiKeys({ dfsLogin: login, dfsPassword: password });
  cache = { ...cache, dfsLogin: login, dfsPassword: password };
}
export async function saveBrightDataCreds(token: string, zone: string): Promise<void> {
  await putApiKeys({ brightdataToken: token, brightdataZone: zone });
  cache = { ...cache, brightdataToken: token, brightdataZone: zone };
}
